import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { fetchPodcast, fetchEpisodes, fetchPodcastTopWords, getPodcastColor, pageData } from '../api.js';
import { applyMeta, podcastMeta } from '../seo.js';
import EpisodeRow from '../components/EpisodeRow.jsx';
import EpisodeFilterBar from '../components/EpisodeFilterBar.jsx';
import { SkeletonList } from '../components/SkeletonRow.jsx';


function PodcastHeaderSkeleton() {
  return (
    <div className="podcast-header" aria-hidden="true">
      <div
        className="skeleton"
        style={{ width: 100, height: 100, borderRadius: 'var(--radius-md)', flexShrink: 0 }}
      />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <div className="skeleton" style={{ height: 20, width: '60%' }} />
        <div className="skeleton" style={{ height: 14, width: '35%' }} />
      </div>
    </div>
  );
}

export default function PodcastDetail() {
  const { podcastSlug, id } = useParams();
  // Support both new /podcast/:podcastSlug and legacy /podcast/:id
  const slug = podcastSlug ?? id;
  const [searchParams] = useSearchParams();

  // Data the prerenderer inlined for this exact URL, when this is the page the
  // visitor (or the crawler) landed on. Seeding initial state from it means the
  // very first render already has the podcast, so the static HTML carries real
  // content and hydration finds the same tree instead of a skeleton.
  const boot = pageData(`podcast:${slug}`);

  const [podcast, setPodcast] = useState(boot?.podcast ?? null);
  const [episodes, setEpisodes] = useState(boot?.episodes ?? []);
  const [nextCursor, setNextCursor] = useState(boot?.nextCursor ?? null);
  const [keywords, setKeywords] = useState(boot?.keywords ?? []);
  const [podcastLoading, setPodcastLoading] = useState(!boot?.podcast);
  const [episodesLoading, setEpisodesLoading] = useState(!boot?.episodes);
  const [loadingMore, setLoadingMore] = useState(false);
  const [podcastError, setPodcastError] = useState(null);
  const [episodesError, setEpisodesError] = useState(null);
  const [artError, setArtError] = useState(false);

  // Collect filter params from URL
  const activeKeywords = searchParams.getAll('keyword');
  const filterParams = {
    sort_by:  searchParams.get('sort_by') || 'published_at',
    order:    searchParams.get('order')   || 'desc',
    search:   searchParams.get('search')  || '',
    keywords: activeKeywords,
  };
  const keywordsKey = activeKeywords.join(',');

  // Stable ref to track in-flight fetch cancel
  const cancelRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    // Skip the skeleton when the prerendered payload already covers this slug,
    // otherwise the page would flash content -> skeleton -> content on load.
    const alreadyResolved = podcast?.podcast_uuid === slug;
    if (!alreadyResolved) setPodcastLoading(true);
    Promise.all([fetchPodcast(slug), fetchPodcastTopWords(slug, 5)])
      .then(([data, topWords]) => {
        if (cancelled) return;
        setPodcast(data);
        setKeywords(topWords);
        setPodcastLoading(false);
      })
      .catch(err => { if (!cancelled) { console.error('Failed to load podcast:', err); setPodcastError(true); setPodcastLoading(false); } });
    return () => { cancelled = true; };
  }, [slug]);

  // Dynamic meta tags + JSON-LD PodcastSeries
  useEffect(() => {
    if (!podcast) return;
    return applyMeta(podcastMeta(podcast));
  }, [podcast]);

  // Reload episodes when filters or podcast id changes.
  // `bootEpisodesValid` is true only on the very first pass of the page the
  // prerenderer built, and only while the URL carries no filters: the inlined
  // list is the unfiltered first page, so any active filter must refetch.
  const bootEpisodesValid = useRef(
    Boolean(boot?.episodes) && !searchParams.toString(),
  );
  useEffect(() => {
    const controller = new AbortController();
    cancelRef.current = controller;
    if (bootEpisodesValid.current) {
      bootEpisodesValid.current = false;
      return () => controller.abort();
    }
    setEpisodesLoading(true);
    setEpisodes([]);
    setNextCursor(null);

    fetchEpisodes(slug, { ...filterParams, limit: 20 })
      .then(data => {
        if (!controller.signal.aborted) {
          setEpisodes(data.episodes ?? []);
          setNextCursor(data.next_cursor ?? null);
          setEpisodesLoading(false);
        }
      })
      .catch(err => {
        if (!controller.signal.aborted) {
          console.error('Failed to load episodes:', err);
          setEpisodesError(true);
          setEpisodesLoading(false);
        }
      });

    return () => controller.abort();
  }, [slug, filterParams.sort_by, filterParams.order, filterParams.search, keywordsKey]);

  const loadMore = useCallback(() => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    fetchEpisodes(slug, { sort_by: filterParams.sort_by, order: filterParams.order, search: filterParams.search, keywords: filterParams.keywords, cursor: nextCursor, limit: 20 })
      .then(data => {
        setEpisodes(prev => [...prev, ...(data.episodes ?? [])]);
        setNextCursor(data.next_cursor ?? null);
        setLoadingMore(false);
      })
      .catch(() => setLoadingMore(false));
  // FE-003: use slug (stable), not id (wrong variable from legacy route)
  // NEW-001: use individual primitive values instead of filterParams object to avoid stale closure
  }, [slug, nextCursor, loadingMore, filterParams.sort_by, filterParams.order, filterParams.search, keywordsKey]);

  // Infinite scroll: observe a sentinel near the end of the list and auto-load the
  // next page as it approaches the viewport. rootMargin prefetches before the user
  // hits the bottom, so the feed keeps flowing with no visible stop.
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!nextCursor) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '600px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [nextCursor, loadMore]);

  // Derive a stable accent color from the podcast UUID
  const accentColor = getPodcastColor(podcast?.podcast_uuid);

  // Co-host label logic: use first names when the podcast has a single named co-host
  // (no "&" or "," means it's not a multi-host or rotating-guest format like SmartLess).
  // Episodes with has_guest=true override back to generic Host/Guest (external guest present).
  const coHostName = podcast?.co_host_name ?? '';
  const isSimpleCoHost = coHostName && !coHostName.includes('&') && !coHostName.includes(',');
  const podcastHostLabel  = isSimpleCoHost ? (podcast.host_name?.split(' ')[0] ?? 'Host')  : 'Host';
  const podcastGuestLabel = isSimpleCoHost ? (coHostName.split(' ')[0] ?? 'Guest') : 'Guest';
  // speaker_breakdown is per-episode, so it beats every podcast-level guess:
  // hosts get named once there is more than one of them, guests stay generic
  // until there is more than one. Episodes without one keep the old fallback.
  const firstNameOf = (n) => n?.split(' ')[0] ?? n;
  const episodeLabels = (ep) => {
    const bd = ep.speaker_breakdown?.length ? ep.speaker_breakdown : null;
    if (bd) {
      const hosts  = bd.filter(s => s.role === 'host');
      const guests = bd.filter(s => s.role === 'guest');
      if (hosts.length === 1 && guests.length === 0)
        return { hostLabel: firstNameOf(hosts[0].name), guestLabel: 'Guest' };
      if (hosts.length === 2 && guests.length === 0)
        return { hostLabel: firstNameOf(hosts[0].name), guestLabel: firstNameOf(hosts[1].name) };
      // Either side holding more than one person breaks the one-to-one keying
      // of the two stored counts, so both sides go generic. "Host / Guest" is
      // only honest on the single-host, single-guest interview.
      if (hosts.length >= 2 || guests.length >= 2)
        return { hostLabel: 'Hosts', guestLabel: 'Guest' };
      return { hostLabel: 'Host', guestLabel: 'Guest' };
    }
    return ep.multi_host
      ? { hostLabel: 'Hosts', guestLabel: 'Guest' }
      : ep.has_guest
        ? { hostLabel: 'Host', guestLabel: 'Guest' }
        : { hostLabel: podcastHostLabel, guestLabel: podcastGuestLabel };
  };

  return (
    <div>
      <nav aria-label="Breadcrumb">
        <Link to="/podcasts" className="back-link">← All podcasts</Link>
      </nav>

      {podcastError && (
        <div className="state-box" role="alert" style={{ marginTop: '1rem' }}>
          <p className="state-box__heading">Failed to load podcast</p>
          <p className="state-box__body">Failed to load. Please try again.</p>
        </div>
      )}

      {podcastLoading ? <PodcastHeaderSkeleton /> : podcast && (
        <div className="podcast-header" style={{ '--card-accent': accentColor }}>
          <div className="podcast-header__accent-bar" aria-hidden="true" />
          {podcast.artwork_url && !artError ? (
            <img
              src={podcast.artwork_url}
              alt=""
              className="podcast-header__art"
              width={100}
              height={100}
              onError={() => setArtError(true)}
            />
          ) : (
            <div className="podcast-header__art podcast-header__art--placeholder" aria-hidden="true">
              {podcast.name?.[0] ?? '?'}
            </div>
          )}
          <div className="podcast-header__info">
            <h1 className="podcast-header__title">{podcast.name}</h1>
            {podcast.episode_count != null && (
              <p className="podcast-header__meta">
                {podcast.episode_count} episode{podcast.episode_count !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      )}

      <EpisodeFilterBar keywords={keywords} accentColor={accentColor} />

      {episodesError && (
        <div className="state-box" role="alert">
          <p className="state-box__heading">Failed to load episodes</p>
          <p className="state-box__body">Failed to load. Please try again.</p>
        </div>
      )}

      {episodesLoading ? (
        <SkeletonList count={6} />
      ) : (
        <>
          {episodes.length > 0 ? (
            <div className="episode-list">
              {episodes.map(ep => (
                <EpisodeRow
                  key={ep.episode_uuid}
                  episode={ep}
                  accentColor={accentColor}
                  {...episodeLabels(ep)}
                />
              ))}
            </div>
          ) : !episodesError && (
            <div className="state-box">
              <p className="state-box__heading">No episodes found</p>
              <p className="state-box__body">Try adjusting your filters or search terms.</p>
            </div>
          )}

          {/* Infinite-scroll sentinel: auto-loads the next page as it nears the viewport. */}
          {nextCursor && (
            <div ref={sentinelRef} className="infinite-sentinel" aria-hidden="true">
              {loadingMore && <SkeletonList count={3} />}
            </div>
          )}
          <div aria-live="polite" className="sr-only">
            {loadingMore ? 'Loading more episodes' : ''}
          </div>
        </>
      )}
    </div>
  );
}
